import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Forward, Mail, MoreHorizontal, Pencil } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { ComposeQuotedBlock } from './types';
import { resolveQuotedBodyHtml } from './quotedBodyDisplay';
import { parseQuotedHeaderMeta, quotedPanelHeading } from './quotedHeaderMeta';
import { patchQuotedBlock, updateQuotedHeaderLine } from './quotedHeaderLines';
import { API_BASE_URL } from '../../../config/apiConfig';
import { Z_INDEX, zStyle } from '../../../lib/zLayers';

export interface QuotedBodyPanelProps {
  quoted: ComposeQuotedBlock;
  emailId?: number;
  partitionAt?: string;
  colors?: Record<string, string>;
  collapsedByDefault?: boolean;
  className?: string;
  /** When set, header fields and body can be edited before send. */
  onQuotedChange?: (quoted: ComposeQuotedBlock) => void;
}

export const QuotedBodyPanel: React.FC<QuotedBodyPanelProps> = ({
  quoted,
  emailId,
  partitionAt,
  collapsedByDefault = false,
  className,
  onQuotedChange,
}) => {
  const editable = Boolean(onQuotedChange);
  const [expanded, setExpanded] = useState(!collapsedByDefault);
  const [openFieldMenu, setOpenFieldMenu] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<{ label: string; value: string } | null>(null);
  const [editingBody, setEditingBody] = useState(false);
  const bodyEditRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const heading = quotedPanelHeading(quoted.headerLines, quoted.mode);
  const metaFields = parseQuotedHeaderMeta(quoted.headerLines.slice(1));
  const isForward = quoted.mode === 'forward';

  const bodyHtml = resolveQuotedBodyHtml(quoted, emailId, API_BASE_URL, partitionAt);

  const Icon = isForward ? Forward : Mail;
  const previewLine =
    metaFields.find((f) => f.label.toLowerCase() === 'subject')?.value ??
    metaFields.find((f) => f.label.toLowerCase() === 'from')?.value ??
    '';

  useEffect(() => {
    if (!openFieldMenu) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpenFieldMenu(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openFieldMenu]);

  const commitHeaderEdit = useCallback(() => {
    if (!editingField || !onQuotedChange) return;
    const nextLines = updateQuotedHeaderLine(
      quoted.headerLines,
      editingField.label,
      editingField.value.trim(),
    );
    onQuotedChange(patchQuotedBlock(quoted, { headerLines: nextLines }));
    setEditingField(null);
  }, [editingField, onQuotedChange, quoted]);

  const commitBodyEdit = useCallback(() => {
    if (!onQuotedChange || !bodyEditRef.current) return;
    onQuotedChange(patchQuotedBlock(quoted, { bodyHtml: bodyEditRef.current.innerHTML }));
    setEditingBody(false);
  }, [onQuotedChange, quoted]);

  const startBodyEdit = useCallback(() => {
    setEditingBody(true);
    setOpenFieldMenu(null);
  }, []);

  useEffect(() => {
    if (!editingBody || !bodyEditRef.current) return;
    bodyEditRef.current.innerHTML = bodyHtml;
    bodyEditRef.current.focus();
  }, [editingBody, bodyHtml]);

  return (
    <div className={cn('mt-1', className)}>
      <div
        className={cn(
          'rounded-lg border overflow-hidden bg-white dark:bg-white',
          isForward ? 'border-indigo-200 dark:border-indigo-200' : 'border-sky-200 dark:border-sky-200',
        )}
      >
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={cn(
            'w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors',
            isForward
              ? 'bg-indigo-50 hover:bg-indigo-100/80 dark:bg-indigo-50 dark:hover:bg-indigo-100/80'
              : 'bg-sky-50 hover:bg-sky-100/80 dark:bg-sky-50 dark:hover:bg-sky-100/80',
          )}
          aria-expanded={expanded}
        >
          <span
            className={cn(
              'flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md',
              isForward
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-100 dark:text-indigo-700'
                : 'bg-sky-100 text-sky-700 dark:bg-sky-100 dark:text-sky-700',
            )}
          >
            <Icon className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
          </span>
          <span className="flex-1 min-w-0">
            <span
              className={cn(
                'block text-[11px] font-bold uppercase tracking-wider',
                isForward
                  ? 'text-indigo-800 dark:text-indigo-800'
                  : 'text-sky-800 dark:text-sky-800',
              )}
            >
              {heading}
            </span>
            {!expanded && previewLine && (
              <span className="block text-xs truncate mt-0.5 text-gray-600 dark:text-gray-600">
                {previewLine}
              </span>
            )}
          </span>
          <span className="flex-shrink-0 text-gray-500 dark:text-gray-500">
            {expanded ? (
              <ChevronUp className="w-4 h-4" aria-hidden />
            ) : (
              <ChevronDown className="w-4 h-4" aria-hidden />
            )}
          </span>
        </button>

        {expanded && (
          <div
            className={cn(
              'email-quoted-panel-readonly select-text border-t bg-white dark:bg-white',
              isForward ? 'border-indigo-100 dark:border-indigo-100' : 'border-sky-100 dark:border-sky-100',
            )}
          >
            {metaFields.length > 0 && (
              <dl className="pl-5 pr-4 pt-4 pb-3 space-y-2 text-sm border-b border-gray-100 dark:border-gray-200">
                {metaFields.map(({ label, value }) => {
                  const isSubject = label.toLowerCase() === 'subject';
                  const isEditing = editingField?.label === label;
                  const menuOpen = openFieldMenu === label;
                  return (
                    <div
                      key={label}
                      className="grid gap-x-3 gap-y-0.5 sm:grid-cols-[4.5rem_1fr]"
                    >
                      <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-500">
                        {label}
                      </dt>
                      <dd className={cn('min-w-0 flex items-start gap-1', isEditing && 'w-full')}>
                        {isEditing ? (
                          <input
                            type="text"
                            autoFocus
                            value={editingField.value}
                            onChange={(e) => setEditingField({ label, value: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                commitHeaderEdit();
                              }
                              if (e.key === 'Escape') setEditingField(null);
                            }}
                            onBlur={() => commitHeaderEdit()}
                            className={cn(
                              'w-full min-w-[12rem] rounded border px-2 py-1 text-sm text-gray-900',
                              'border-gray-300 dark:border-gray-400 bg-white dark:bg-white',
                            )}
                          />
                        ) : (
                          <>
                            <span
                              className={cn(
                                'flex-1 min-w-0 break-words text-gray-800 dark:text-gray-800',
                                isSubject && 'font-semibold text-gray-900 dark:text-gray-900',
                                editable && 'cursor-text',
                              )}
                              title={editable ? 'Double-click to edit' : undefined}
                              onDoubleClick={
                                editable
                                  ? () => setEditingField({ label, value })
                                  : undefined
                              }
                            >
                              {value}
                            </span>
                            {editable && (
                              <div className="relative shrink-0">
                                <button
                                  type="button"
                                  className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-100"
                                  title={`Actions for ${label}`}
                                  aria-label={`Actions for ${label}`}
                                  aria-expanded={menuOpen}
                                  onClick={() => setOpenFieldMenu(menuOpen ? null : label)}
                                >
                                  <MoreHorizontal className="w-4 h-4" aria-hidden />
                                </button>
                                {menuOpen && (
                                  <div
                                    ref={menuRef}
                                    className="absolute right-0 top-full mt-1 min-w-[9rem] rounded-lg border border-gray-200 bg-white py-1 text-xs shadow-lg dark:border-gray-200 dark:bg-white"
                                    style={zStyle(Z_INDEX.localDropdown)}
                                  >
                                    <button
                                      type="button"
                                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-gray-800 hover:bg-gray-50 dark:text-gray-800 dark:hover:bg-gray-50"
                                      onClick={() => {
                                        setEditingField({ label, value });
                                        setOpenFieldMenu(null);
                                      }}
                                    >
                                      <Pencil className="w-3.5 h-3.5 shrink-0" aria-hidden />
                                      Edit
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            )}

            {bodyHtml && (
              <div className="relative">
                {editable && !editingBody && (
                  <div className="absolute top-2 right-2 z-10">
                    <button
                      type="button"
                      onClick={startBodyEdit}
                      className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-200 dark:bg-white dark:text-gray-800"
                    >
                      <Pencil className="w-3 h-3" aria-hidden />
                      Edit message
                    </button>
                  </div>
                )}
                {editingBody ? (
                  <div className="p-2">
                    <div
                      ref={bodyEditRef}
                      contentEditable
                      suppressContentEditableWarning
                      className="email-quoted-body min-h-[8rem] overflow-x-auto rounded border border-indigo-200 bg-white px-3 py-2.5 text-gray-900 outline-none focus:ring-2 focus:ring-indigo-400/50 dark:border-indigo-200 dark:bg-white dark:text-gray-900"
                    />
                    <div className="mt-2 flex justify-end gap-2">
                      <button
                        type="button"
                        className="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
                        onClick={() => setEditingBody(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="rounded bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-700"
                        onClick={commitBodyEdit}
                      >
                        Done
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className={cn(
                      'email-quoted-body overflow-x-auto bg-white px-3 py-2.5 text-gray-900 dark:bg-white dark:text-gray-900',
                      editable && 'cursor-text',
                    )}
                    title={editable ? 'Double-click to edit message' : undefined}
                    onDoubleClick={editable ? startBodyEdit : undefined}
                    dangerouslySetInnerHTML={{ __html: bodyHtml }}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
